import {
  useState, useCallback, useRef, useContext,
} from 'react';
import parse from 'autosuggest-highlight/parse';
import {
  Autocomplete, Grid, Typography, Paper as MuiPaper,
  TextField, ListItem,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useDebounce } from '@client/hooks/utils.hooks';
import { useSetStatus } from '@client/hooks/formik.hooks';
import { getLocationData } from '@client/listeners/lib/requests';
import { showSnackbar } from '@client/listeners/lib/snackbar';
import { AutosaveContext } from '../common/AutosaveContext';
import type { Writable } from 'type-fest';
import type { FormikHelpers } from 'formik';
import type { PaperProps, TextFieldProps } from '@mui/material';

type FetchLocationDataCallbackData = Awaited<ReturnType<typeof getLocationData>>;

const fetchLocationData = async ({ placeId, callback }: { placeId: string, callback: (data: FetchLocationDataCallbackData) => void }) => {
  try {
    const locationData = await getLocationData(placeId);

    callback(locationData);
  } catch (e) {
    showSnackbar(e, 'error');
  }
};

const Paper = (props: PaperProps) => (
  <MuiPaper
    {...props}
    elevation={4}
  />
);

const useStyles = makeStyles()((theme) => ({
  container: {
    zIndex: theme.zIndex.appBar - 1,
  },
  icon: {
    color: theme.palette.text.primary,
    marginRight: theme.spacing(2),
  },
}));

export type GoogleSearchSelectHandler = (value?: google.maps.places.AutocompletePrediction, location?: google.maps.LatLng) => void;

export interface GoogleSearchBaseProps {
  readonly id: string;
  readonly includeLatLngField: boolean;
  readonly onSearchSelect: GoogleSearchSelectHandler;
  readonly setFieldValue: FormikHelpers<unknown>['setFieldValue'];
  readonly type?: string;
  readonly updateAsyncFields?: string[];
  readonly disabled?: boolean;
  readonly filterOptions?: (x: any) => any;
  readonly googlePlaceIdName?: string;
  readonly label?: React.ReactNode;
  readonly readOnly?: boolean;
}

export const GoogleSearchBase: React.FC<GoogleSearchBaseProps & TextFieldProps> = ({
  id, includeLatLngField, onSearchSelect, setFieldValue,
  type, updateAsyncFields, className = '',
  disabled = false,
  filterOptions = (x) => x,
  googlePlaceIdName = 'googlePlaceId',
  InputProps = {},
  value = '',
  ...restProps
}) => {
  const { classes } = useStyles();
  const setStatus = useSetStatus();

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [autosave] = useContext(AutosaveContext) || [];

  const autocompleteService = useRef<google.maps.places.AutocompleteService>(null);
  const placesService = useRef<google.maps.places.PlacesService>(null);

  const defaultValueRef = useRef<string>(null);

  if (!defaultValueRef.current) {
    defaultValueRef.current = value as string;
  }

  if (!autocompleteService.current && window.google) {
    autocompleteService.current = new window.google.maps.places.AutocompleteService();
  }

  if (!placesService.current && window.google) {
    placesService.current = new window.google.maps.places.PlacesService(document.createElement('div'));
  }

  const handleAutoSave = useCallback(() => {
    if (autosave) {
      autosave();
    }
  }, [autosave]);

  const handleAsyncSave = useCallback((responseData: FetchLocationDataCallbackData) => {
    updateAsyncFields.forEach((field) => {
      void setFieldValue(field, responseData[field]);

      if (field === 'timezoneOverride') {
        setStatus({ timezone: responseData[field] });
      }
    });

    setLoading(false);
    handleAutoSave();
  }, [handleAutoSave, setFieldValue, setStatus, updateAsyncFields]);

  const handleSelect = useCallback((e, val: google.maps.places.AutocompletePrediction) => {
    if (val) {
      if (includeLatLngField && val.place_id) {
        placesService.current.getDetails({ placeId: val.place_id, fields: ['geometry.location'] }, (result) => {
          void setFieldValue('latLng', {
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng(),
          });

          void setFieldValue(googlePlaceIdName, val.place_id);
          void setFieldValue(restProps.name, val.description);
          onSearchSelect(val, result.geometry.location);

          if (updateAsyncFields && updateAsyncFields.length) {
            void fetchLocationData({ placeId: val.place_id, callback: handleAsyncSave });
          } else {
            setLoading(false);
            handleAutoSave();
          }
        });
      } else {
        void setFieldValue(googlePlaceIdName, val.place_id);
        void setFieldValue(restProps.name, val.description);
        onSearchSelect(val, null);

        if (updateAsyncFields && updateAsyncFields.length) {
          void fetchLocationData({ placeId: val.place_id, callback: handleAsyncSave });
        } else {
          setLoading(false);
          handleAutoSave();
        }
      }
    } else {
      setLoading(false);

      void setFieldValue('latLng', null);

      void setFieldValue(googlePlaceIdName, null);
      void setFieldValue(restProps.name, null);
      onSearchSelect(null, null);

      handleAutoSave();
    }
  }, [includeLatLngField, setFieldValue, googlePlaceIdName, restProps.name,
    onSearchSelect, updateAsyncFields, handleAsyncSave, handleAutoSave]);

  const fetch = useDebounce<typeof autocompleteService.current.getPlacePredictions>((val, callback) => {
    setLoading(true);

    return autocompleteService.current.getPlacePredictions(val, callback);
  }, 500);

  const handleInputChange = useCallback((_, v: string) => {
    if (!autocompleteService.current || !placesService.current) {
      return undefined;
    }

    if (v === '') {
      setOptions([]);
      return undefined;
    }

    let searchInput: Writable<google.maps.places.AutocompletionRequest> = {
      input: v,
    };

    if (type) {
      searchInput.types = [type];
    }

    const fetchData = async () => fetch(searchInput, (results) => {
      setOptions(results || []);
    });

    void fetchData();

    return undefined;
  }, [type, fetch]);

  return (
    <Autocomplete
      className={className}
      disabled={disabled}
      defaultValue={defaultValueRef.current}
      filterOptions={filterOptions}
      getOptionLabel={(option: string | { description: string }) => (typeof option === 'string' ? option : option.description)}
      id={id}
      loading={loading}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      options={options}
      PaperComponent={Paper}
      value={value}
      readOnly={!!restProps.readOnly}
      autoComplete
      freeSolo
      includeInputInList
      renderInput={({ InputProps: AutocompleteInputProps, ...restParams }) => (
        <TextField
          {...restParams}
          {...restProps}
          InputProps={{
            ...AutocompleteInputProps,
            ...InputProps,
          }}
          id={null}
          type="search"
          fullWidth
        />
      )}
      renderOption={(props, option: google.maps.places.AutocompletePrediction) => {
        const matches = option.structured_formatting.main_text_matched_substrings;

        if (!matches) {
          return null;
        }

        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length]),
        );

        return (
          <ListItem
            className={classes.container}
            {...props}
          >
            <Grid
              alignItems="center"
              wrap="nowrap"
              container
            >
              <Grid item>
                <LocationOnIcon className={classes.icon} />
              </Grid>

              <Grid item>
                {parts.map((part) => (
                  <Typography
                    variant={part.highlight ? 'body2' : 'body1'}
                    display="inline"
                    key={part.text}
                  >
                    {part.text}
                  </Typography>
                ))}

                <Typography variant="body1">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </ListItem>
        );
      }}
    />
  );
};
